import React from 'react';
import styled from 'styled-components';

import { CleaningPrestation, UniverseReference, User } from '@front/common/src/types/commons';
import { BasketState } from '@front/common/src/ducks/common/basket';
import { getBasketMode } from '@front/common/src/ducks/customer/basket';
import colors from '@front/common/src/utils/colors';

import useGetPrices from '@front/customer/src/hooks/use-get-prices';

import { WithClassNameProps } from '@front/web-ui/src/components/types';
import Skeleton from '@front/web-ui/src/components/skeleton/skeleton';

import { trackMembershipEvent } from '../../../../utils/tracking';
import { MembershipCardSwitch as StyledSwitch, MembershipCardContainer } from '../../membership-card/common';
import { useAppSelector } from '../../../../store';

import MembershipCardContent from './membership-card-content/membership-card-content';

export const MEMBERSHIP_CLEANING_PRICES_LOADED_TEST_ID = 'membership-cleaning-prices-loaded-test-id';

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SwitchContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 1;
`;

type MembershipCardProps = WithClassNameProps & {
  isMembershipRequested: boolean;
  setIsMembershipRequested: (isRequested: boolean) => void;
  openMembershipPaywall: () => void;
  pathname: string;
  user?: User;
  universeReference: UniverseReference;
  canSubscribeToMembership: boolean;
  duration: BasketState['duration'];
  cleaningPrestation?: CleaningPrestation;
};

const MembershipCard = ({
  isMembershipRequested,
  setIsMembershipRequested,
  openMembershipPaywall,
  pathname,
  user,
  universeReference,
  canSubscribeToMembership,
  duration,
  cleaningPrestation,
  className,
}: MembershipCardProps) => {
  const { prices, pricesError } = useGetPrices();
  const mode = useAppSelector(getBasketMode);

  const onChange = () => {
    const newSwitchValue = !isMembershipRequested;

    setIsMembershipRequested(newSwitchValue);
    trackMembershipEvent({
      eventType: 'MembershipSwitchClick',
      user,
      universeReference,
      mode,
      path: pathname,
      additionalData: {
        turningOn: newSwitchValue,
      },
    });
  };

  const onReadMorePress = () => {
    trackMembershipEvent({
      eventType: 'MoreDetailsMembershipClick',
      user,
      universeReference,
      mode,
      path: pathname,
    });

    openMembershipPaywall();
  };

  return (
    <MembershipCardContainer className={className}>
      {!prices || !!pricesError ? (
        <SkeletonWrapper>
          <Skeleton linesWidth={['100%', '100%', '100px']} color={colors.pink40} />
        </SkeletonWrapper>
      ) : (
        <div data-testid={MEMBERSHIP_CLEANING_PRICES_LOADED_TEST_ID}>
          <MembershipCardContent
            isMembershipRequested={isMembershipRequested}
            user={user}
            getMoreInfoPress={onReadMorePress}
            cleaningPrestation={cleaningPrestation}
            duration={duration}
            reduction={prices.member.discount}
          />
        </div>
      )}
      {canSubscribeToMembership && (
        <SwitchContainer>
          <label htmlFor='membership-switch'>
            <StyledSwitch
              id='membership-switch'
              name='membership-switch'
              onChange={onChange}
              isChecked={isMembershipRequested}
              value={String(isMembershipRequested)}
              variant='pink'
            />
          </label>
        </SwitchContainer>
      )}
    </MembershipCardContainer>
  );
};

export default MembershipCard;
